import { Card, Divider, Grid, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import FormButtonOptions from '../../../FormFields/FormButtonOptions';
import FormTextField from '../../../FormFields/FormTextField';
import FormTitle from '../../../FormFields/FormTitle';
import FormStaffInfoField from '../../../FormFields/StaffInfoField/FormStaffInfoField';
import FormStaffAccessInfoField from '../../../FormFields/StaffInfoField/FormStaffInfoFieldAccess';
import { BUSINESS_DEVELOPMENT } from '../../../formQuestionsEnum';
import { SOCIAL_MEDIA_OPTIONS } from '../../../optionEnums';
import { formCardStyle } from '../../../TransitionStyles';
import { validateWebsiteDomain } from '../../../ValidateFunctions';

MarketingProgramsForm.propTypes = {
  permissions: PropTypes.object,
};

const fieldIds = [
  BUSINESS_DEVELOPMENT.ADMISSIONS_DIRECTOR.id,
  BUSINESS_DEVELOPMENT.MARKETER.id,
  BUSINESS_DEVELOPMENT.CLINICAL_LIAISON.id,
  BUSINESS_DEVELOPMENT.WEBSITE_DOMAIN.id,
  BUSINESS_DEVELOPMENT.WEBSITE_DOMAIN_ACCESS.id,
  BUSINESS_DEVELOPMENT.GOOGLE_BUSINESS_ACCESS.id,
  BUSINESS_DEVELOPMENT.SOCIAL_MEDIA_OPTIONS.id,
  BUSINESS_DEVELOPMENT.SOCIAL_MEDIA_ACCESS.id,
];

export default function MarketingProgramsForm({ permissions }) {
  const [focusedField, setFocusedField] = useState(null);

  const showForm = fieldIds.some((id) => id in permissions);

  return (
    showForm && (
      <Card sx={formCardStyle}>
        <Typography sx={{ mb: 1 }} align="center" variant="body1" color="info.main">
          Marketing Programs
        </Typography>
        <Divider sx={{ mb: 1, mt: 2, borderWidth: 1 }} />
        <Grid container spacing={1.5}>
          {[
            BUSINESS_DEVELOPMENT.MARKETER.id,
            BUSINESS_DEVELOPMENT.CLINICAL_LIAISON.id,
            BUSINESS_DEVELOPMENT.ADMISSIONS_DIRECTOR.id,
          ].some((id) => id in permissions) && (
            <>
              {BUSINESS_DEVELOPMENT.MARKETER.id in permissions && (
                <Grid item xs={12}>
                  <FormStaffInfoField
                    fieldName={BUSINESS_DEVELOPMENT.MARKETER.fieldName}
                    fieldAttributes={{
                      isEditable: permissions[BUSINESS_DEVELOPMENT.MARKETER.id],
                      questionTitle: 'Marketer',
                      fieldName: BUSINESS_DEVELOPMENT.MARKETER.fieldName,
                    }}
                    focusedField={focusedField}
                    setFocusedField={setFocusedField}
                  />
                </Grid>
              )}
              {BUSINESS_DEVELOPMENT.CLINICAL_LIAISON.id in permissions && (
                <Grid item xs={12}>
                  <FormStaffInfoField
                    fieldName={BUSINESS_DEVELOPMENT.CLINICAL_LIAISON.fieldName}
                    fieldAttributes={{
                      isEditable: permissions[BUSINESS_DEVELOPMENT.CLINICAL_LIAISON.id],
                      questionTitle: 'Clinical liaison',
                      fieldName: BUSINESS_DEVELOPMENT.CLINICAL_LIAISON.fieldName,
                    }}
                    focusedField={focusedField}
                    setFocusedField={setFocusedField}
                  />
                </Grid>
              )}
              {BUSINESS_DEVELOPMENT.ADMISSIONS_DIRECTOR.id in permissions && (
                <Grid item xs={12}>
                  <FormStaffInfoField
                    fieldName={BUSINESS_DEVELOPMENT.ADMISSIONS_DIRECTOR.fieldName}
                    fieldAttributes={{
                      isEditable: permissions[BUSINESS_DEVELOPMENT.ADMISSIONS_DIRECTOR.id],
                      questionTitle: 'Admissions director',
                      fieldName: BUSINESS_DEVELOPMENT.ADMISSIONS_DIRECTOR.fieldName,
                    }}
                    focusedField={focusedField}
                    setFocusedField={setFocusedField}
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <Divider sx={{ mb: 2, mt: 2, borderWidth: 1 }} />
              </Grid>
            </>
          )}
          {[BUSINESS_DEVELOPMENT.WEBSITE_DOMAIN.id, BUSINESS_DEVELOPMENT.WEBSITE_DOMAIN_ACCESS.id].some(
            (id) => id in permissions
          ) && (
            <>
              <Grid item xs={12}>
                <FormTitle fieldAttributes={{ questionTitle: 'Website domain', fieldType: 'title' }} />
              </Grid>
              {BUSINESS_DEVELOPMENT.WEBSITE_DOMAIN.id in permissions && (
                <Grid item xs={12}>
                  <FormTextField
                    fieldName={BUSINESS_DEVELOPMENT.WEBSITE_DOMAIN.fieldName}
                    focusedField={focusedField}
                    setFocusedField={setFocusedField}
                    fieldAttributes={{
                      isEditable: permissions[BUSINESS_DEVELOPMENT.WEBSITE_DOMAIN.id],
                      validation: {
                        pattern: { ...validateWebsiteDomain },
                      },
                    }}
                  />
                </Grid>
              )}
              {BUSINESS_DEVELOPMENT.WEBSITE_DOMAIN_ACCESS.id in permissions && (
                <Grid item xs={12}>
                  <FormStaffAccessInfoField
                    fieldName={BUSINESS_DEVELOPMENT.WEBSITE_DOMAIN_ACCESS.fieldName}
                    toggleFieldName={`${BUSINESS_DEVELOPMENT.WEBSITE_DOMAIN_ACCESS.fieldName}.isTransferable`}
                    toggleLabel="Transferable"
                    fieldAttributes={{
                      isEditable: permissions[BUSINESS_DEVELOPMENT.WEBSITE_DOMAIN_ACCESS.id],
                      questionTitle: 'Who has access',
                    }}
                    focusedField={focusedField}
                    setFocusedField={setFocusedField}
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <Divider sx={{ mb: 2, mt: 2, borderWidth: 1 }} />
              </Grid>
            </>
          )}
          {BUSINESS_DEVELOPMENT.GOOGLE_BUSINESS_ACCESS.id in permissions && (
            <>
              <Grid item xs={12}>
                <FormTitle fieldAttributes={{ questionTitle: 'Google business profile', fieldType: 'title' }} />
                <FormStaffAccessInfoField
                  fieldName={BUSINESS_DEVELOPMENT.GOOGLE_BUSINESS_ACCESS.fieldName}
                  showToggle={false}
                  fieldAttributes={{
                    isEditable: permissions[BUSINESS_DEVELOPMENT.GOOGLE_BUSINESS_ACCESS.id],
                    questionTitle: 'Who has access',
                  }}
                  focusedField={focusedField}
                  setFocusedField={setFocusedField}
                />
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ mb: 2, mt: 2, borderWidth: 1 }} />
              </Grid>
            </>
          )}
          {[BUSINESS_DEVELOPMENT.SOCIAL_MEDIA_OPTIONS.id, BUSINESS_DEVELOPMENT.SOCIAL_MEDIA_ACCESS.id].some(
            (id) => id in permissions
          ) && (
            <>
              <Grid item xs={12}>
                <FormTitle fieldAttributes={{ questionTitle: 'Social media', fieldType: 'title' }} />
              </Grid>
              {BUSINESS_DEVELOPMENT.SOCIAL_MEDIA_OPTIONS.id in permissions && (
                <Grid item xs={12}>
                  <FormButtonOptions
                    fieldName={BUSINESS_DEVELOPMENT.SOCIAL_MEDIA_OPTIONS.fieldName}
                    fieldAttributes={{
                      isEditable: permissions[BUSINESS_DEVELOPMENT.SOCIAL_MEDIA_OPTIONS.id],
                      options: SOCIAL_MEDIA_OPTIONS,
                    }}
                    isMultiSelect
                  />
                </Grid>
              )}
              {BUSINESS_DEVELOPMENT.SOCIAL_MEDIA_ACCESS.id in permissions && (
                <Grid item xs={12}>
                  <FormStaffAccessInfoField
                    fieldName={BUSINESS_DEVELOPMENT.SOCIAL_MEDIA_ACCESS.fieldName}
                    toggleFieldName={`${BUSINESS_DEVELOPMENT.SOCIAL_MEDIA_ACCESS.fieldName}.isTransferable`}
                    toggleLabel="Transferable"
                    fieldAttributes={{
                      isEditable: permissions[BUSINESS_DEVELOPMENT.SOCIAL_MEDIA_ACCESS.id],
                      questionTitle: 'Who has access',
                    }}
                    focusedField={focusedField}
                    setFocusedField={setFocusedField}
                  />
                </Grid>
              )}
            </>
          )}
        </Grid>
      </Card>
    )
  );
}
